<template>
    <div>
        <UniversityShowDocumentsFilter v-model:course="course" />

        <Spinner
            v-if="searchInProgress"
            class="my-32"
            appearance="dots"
            size="xl"
            color="black"
        ></Spinner>

        <template v-else>
            <EmptySearchResult v-if="documentsTotalCount === 0" />

            <div
                v-if="documentsTotalCount > 0"
                class="space-y-2 mb-5 max-md:container"
            >
                <CardSearchResult
                    v-for="(item, i) in documentsList"
                    :key="item.id"
                    v-memo="[maxSM]"
                    :context="{
                        type: 'document',
                        avg_star_score: item.avg_star_score,
                        ratings_count: item.ratings_count,
                        user_rated: item.user_rated,
                        questions: item.number_questions,
                        has_ai_content: item.has_ai_content,
                        downloads: item.downloads,
                    }"
                    :created-at="item.uploaded_at"
                    :course-name="item.course_name"
                    :course-id="item.course_id"
                    :type="item.file_content_type"
                    :img-src="item.file_preview_link"
                    enable-links
                    :title="item.filename"
                    title-tag="h2"
                    :href="getDocumentLink(item.id, item.filename, 'document')"
                    clickable
                    :mode="maxSM ? 'stack' : 'default'"
                />

                <Paginate
                    v-if="documentsLastPage > 1"
                    v-model="currentPage"
                    :page-count="documentsLastPage"
                    :page-range="6"
                    :margin-pages="0"
                    :click-handler="onPageChange"
                    hide-prev-next
                    first-last-button
                    :first-button-text="firstPage"
                    :last-button-text="lastPage"
                    break-view-class="hidden"
                    prev-text='<div class="flex cursor-pointer"><span class="mr-2 icon icon-chevron-left"></span></div>'
                    next-text='<div class="flex cursor-pointer"><span class="ml-2 icon icon-chevron-right"></span></div>'
                    break-view-link-class="cursor-default text-black"
                    container-class="flex items-center justify-center w-full text-primary"
                    active-class="text-black cursor-default"
                    page-class="mx-2 text-lg cursor-pointer"
                    class="!mt-5"
                />
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEqual } from 'lodash-es';
import Paginate from 'vuejs-paginate-next';
import { getCourseById } from '@/api/backend/course';
import EmptySearchResult from '@components/EmptySearchResult';
import CardSearchResult from '@components/CardSearchResult';
import { useScreenSize } from '@/composables/screenSize';
import { getDocumentLink } from '@/router/links';
import { useTranslation } from 'i18next-vue';
import UniversityShowDocumentsFilter from './UniversityShowDocumentsFilter.vue';

export default {
    name: 'UniversityShowDocuments',
    components: {
        EmptySearchResult,
        UniversityShowDocumentsFilter,
        CardSearchResult,
        Paginate,
    },
    setup() {
        const { maxSM } = useScreenSize();
        const { t } = useTranslation('university');
        return {
            getDocumentLink,
            maxSM,
            t,
        };
    },
    data() {
        return {
            course: null,
        };
    },
    computed: {
        ...mapGetters({
            documentsList: 'university/documentsList',
            searchInProgress: 'university/documentsProgress',
            documentsTotalCount: 'university/documentsTotalCount',
            documentsLastPage: 'university/documentsLastPage',
            university: 'university/currentUniversity',
            params: 'university/searchParams',
            locale: 'ui/getLocale',
        }),
        currentPage() {
            return parseInt(this.params.p || 1, 10);
        },
        firstPage() {
            let hideGoToFirst = '';
            if (this.currentPage === 1) {
                hideGoToFirst = 'hidden';
            }

            return (
                `<span class="block text-blue-600 mt-2 ${hideGoToFirst}">` +
                `<i class="icon icon icon-chevron-left -mr-4"></i>` +
                `<i class="icon icon icon-chevron-left"></i>` +
                `</span>`
            );
        },
        lastPage() {
            let hideGoToLast = '';
            if (this.currentPage === this.documentsLastPage) {
                hideGoToLast = 'hidden';
            }

            return (
                `<span class="block text-blue-600 mt-2 ${hideGoToLast}">` +
                `<i class="icon icon icon-chevron-right"></i>` +
                `<i class="icon icon icon-chevron-right -ml-4"></i>` +
                `</span>`
            );
        },
    },
    watch: {
        course(course) {
            // if course is already set as param, don't trigger udpate
            // eslint-disable-next-line eqeqeq
            if (this.params.course_id == course?.id) return;

            this.$store.dispatch('university/applyParams', {
                course_id: course?.id,
            });
        },
        params: {
            deep: true,
            immediate: true,
            handler: function paramsWatcher(params, oldParams) {
                if (params.content === 'documents' && !isEqual(params, oldParams)) {
                    this.getDocuments();
                }
            },
        },
    },
    created() {
        if (this.params.course_id) {
            this.loadCourse(this.params.course_id);
        }
    },
    methods: {
        async loadCourse(id) {
            this.course = (await getCourseById(id)).data;
        },
        getDocuments() {
            this.$store.dispatch('university/retrieveDocuments', {
                p: this.currentPage,
                sort_type_documents: this.sortBy,
                ...this.params,
                content: 'documents',
                university_id: this.university.id,
            });
        },
        onPageChange(page) {
            this.$store.dispatch('university/applyParams', {
                p: page,
            });
        },
    },
};
</script>
